import React, { useState, useMemo } from 'react';
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    isWithinInterval,
    isAfter,
    isBefore,
    parseISO
} from 'date-fns';
import { de } from 'date-fns/locale';

/**
 * CompactRangePicker - Ein edler, kompakter Kalender zur Auswahl von Zeitspannen.
 * 
 * @param {Object} props
 * @param {string} props.startDate - Startdatum (YYYY-MM-DD)
 * @param {string} props.endDate - Enddatum (YYYY-MM-DD)
 * @param {Function} props.onSelect - Callback(start, end)
 */
export default function CompactRangePicker({ startDate, endDate, onSelect, onClear }) {
    // Hilfsfunktion: String zu Date (Sicherer Umgang mit Date-Objekten und ISO-Strings)
    const toDate = (val) => {
        if (!val) return null;
        if (val instanceof Date) return val;
        if (typeof val === 'string') return parseISO(val.replace(' ', 'T'));
        return null;
    };

    const today = new Date();
    const [viewDate, setViewDate] = useState(toDate(startDate) || today);
    const [hoverDate, setHoverDate] = useState(null);

    // Lokaler State für die aktuelle Auswahl-Phase (null = Suche Start, date = Suche Ende)
    const [tempStart, setTempStart] = useState(null);


    const sDate = useMemo(() => tempStart || toDate(startDate), [startDate, tempStart]);
    const eDate = useMemo(() => tempStart ? null : toDate(endDate), [endDate, tempStart]);

    // Kalender-Berechnung
    const days = useMemo(() => {
        const start = startOfWeek(startOfMonth(viewDate), { weekStartsOn: 1 });
        const end = endOfWeek(endOfMonth(viewDate), { weekStartsOn: 1 });
        return eachDayOfInterval({ start, end });
    }, [viewDate]);

    const handleDateClick = (date) => {
        const dateStr = format(date, 'yyyy-MM-dd');

        if (!tempStart) {
            // Phase 1: Startdatum wurde gewählt
            setTempStart(date);
            // Wir informieren das Parent schonmal über den neuen Start, 
            // setzen aber Enddatum noch auf null um die Phase 2 zu signalisieren
            onSelect(dateStr, null);
        } else {
            // Phase 2: Enddatum wurde gewählt
            const finalStart = isBefore(date, tempStart) ? dateStr : format(tempStart, 'yyyy-MM-dd');
            const finalEnd = isBefore(date, tempStart) ? format(tempStart, 'yyyy-MM-dd') : dateStr;

            setTempStart(null);
            onSelect(finalStart, finalEnd);
        }
    };

    const isInRange = (date) => {
        if (!sDate) return false;
        const end = eDate || hoverDate;
        if (!end) return false;

        const [min, max] = isBefore(sDate, end) ? [sDate, end] : [end, sDate];
        return (isSameDay(date, min) || isSameDay(date, max) || isWithinInterval(date, { start: min, end: max }));
    };

    return (
        <div className="compact-range-picker" onClick={e => e.stopPropagation()}>
            <div className="crp-header">
                <button type="button" className="crp-nav-btn" onClick={() => setViewDate(subMonths(viewDate, 1))}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
                <div className="crp-month-label">{format(viewDate, 'MMMM yyyy', { locale: de })}</div>
                <button type="button" className="crp-nav-btn" onClick={() => setViewDate(addMonths(viewDate, 1))}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6" /></svg>
                </button>
            </div>

            <div className="crp-weekday-row">
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
                    <div key={d} className="crp-weekday">{d}</div>
                ))}
            </div>

            <div className="crp-days-grid">
                {days.map(day => {
                    const isSelectedStart = sDate && isSameDay(day, sDate);
                    const isSelectedEnd = eDate && isSameDay(day, eDate);
                    const isToday = isSameDay(day, today);
                    const inRange = isInRange(day);
                    const inMonth = isSameMonth(day, viewDate);

                    let classNames = "crp-day";
                    if (!inMonth) classNames += " off-month";
                    if (isSelectedStart) classNames += " is-start";
                    if (isSelectedEnd) classNames += " is-end";
                    if (isSelectedStart && isSelectedEnd) classNames += " is-single";
                    if (inRange && !isSelectedStart && !isSelectedEnd) classNames += " in-range";
                    if (isToday) classNames += " is-today";

                    return (
                        <div
                            key={day.toString()}
                            className={classNames}
                            onClick={() => handleDateClick(day)}
                            onMouseEnter={() => tempStart && setHoverDate(day)}
                            onMouseLeave={() => setHoverDate(null)}
                        >
                            <span>{format(day, 'd')}</span>
                        </div>
                    );
                })}
            </div>

            <div className="crp-footer">
                <span className="crp-footer-status">
                    {!tempStart ? "Startdatum wählen" : "Enddatum wählen"}
                </span>
                {(startDate || endDate) && onClear && (
                    <button 
                        type="button" 
                        className="crp-reset-btn" 
                        onClick={(e) => {
                            e.stopPropagation();
                            onClear();
                        }}
                    >
                        Zurücksetzen
                    </button>
                )}
            </div>
        </div>
    );
}
